import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  inject,
  OnInit,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AbstractDynamicComponent } from '@components/abstracts';
import { FormulaireRdv, FormValid } from '@components/formulaire-rdv/models';
import { TranslatePipe } from '@ngx-translate/core';
import { AppointmentDetailsComponent } from '@shared/components/appointment-details';
import { ButtonComponent } from '@shared/components/button';
import { SvgComponent } from '@shared/components/svg';
import { TitleComponent } from '@shared/components/title';
import { HandleHtmlLinksDirective } from '@shared/directives';
import { ConfiguratorLinkService } from '@shared/services/configurator-link';
import { CurrentLanguageService } from '@shared/services/language';
import { LogsService } from '@shared/services/logs';
import { RdvsService } from '@shared/services/rdvs';
import { Rdvs } from '@shared/services/rdvs/models';
import { isEmailValid, normalizeAndHash, validatePhoneByCountry } from '@shared/utils';
import { CommonStore, FunnelStore } from '@stores';
import { combineLatest, EMPTY, map, of, switchMap, take } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

@Component({
  selector: 'app-formulaire-rdv',
  imports: [
    TitleComponent,
    SvgComponent,
    NgClass,
    ButtonComponent,
    TranslatePipe,
    AppointmentDetailsComponent,
    HandleHtmlLinksDirective,
  ],
  templateUrl: './formulaire-rdv.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormulaireRdvComponent extends AbstractDynamicComponent<FormulaireRdv> implements OnInit {
  readonly formStart = viewChild<ElementRef<HTMLDivElement>>('formStart');
  readonly firstName = signal<string>('');
  readonly lastName = signal<string>('');
  readonly phone = signal<string>('');
  readonly email = signal<string>('');
  readonly showNote = signal(true);
  readonly note = signal<string>('');
  readonly subscribeEmail = signal(false);
  readonly subscribeSms = signal(false);
  readonly isFormSubmitted = signal(false);
  readonly isFormValid: Signal<FormValid> = computed(() => {
    let result: FormValid = {
      status: 'OK',
    };
    const errors: FormValid['errors'] = {};

    if (this.firstName().length < 3) {
      errors.firstName = 'GLOBAL.FORMS.FIRST_NAME_MIN_LENGTH_ERROR';
    }

    if (this.lastName().length < 2) {
      errors.lastName = 'GLOBAL.FORMS.LAST_NAME_MIN_LENGTH_ERROR';
    }

    if (this.email().length === 0) {
      errors.email = 'GLOBAL.FORMS.EMAIL_REQUIRED_ERROR';
    } else if (!isEmailValid(this.email())) {
      errors.email = 'GLOBAL.FORMS.EMAIL_INVALID_ERROR';
    }
    if (this.phone().length === 0) {
      errors.phone = 'GLOBAL.FORMS.PHONE_REQUIRED_ERROR';
    } else if (!validatePhoneByCountry(this.phone(), this.language.region)) {
      errors.phone = 'GLOBAL.FORMS.PHONE_FORMAT_ERROR';
    }

    if (Object.keys(errors).length > 0) {
      result = {
        status: 'KO',
        errors: errors,
      };
    }

    return result;
  });
  readonly isSending = signal(false);
  readonly serverError = signal(false);
  private readonly router = inject(Router);
  private readonly funnelStore = inject(FunnelStore);
  readonly storeSelected = this.funnelStore.select('storeSelected');
  readonly slotSelected = this.funnelStore.select('slotSelected');
  private readonly userInfo = this.funnelStore.select('userInfo');
  private readonly rdvsService = inject(RdvsService);
  private readonly logs = inject(LogsService);
  private readonly language = inject(CurrentLanguageService).language;
  private readonly configuratorLink = inject(ConfiguratorLinkService);
  private readonly commonStore = inject(CommonStore);
  private readonly baseUrl = this.commonStore.select('baseUrl');
  private readonly appointmentFunnel = this.commonStore.select('appointmentFunnel');

  private readonly emailAndPhoneSha256: Signal<{ email: string; phone: string }> = toSignal(
    combineLatest([
      toObservable(this.email), // reacts to email changes
      toObservable(this.phone), // reacts to email changes
      toObservable(this.isFormValid), // reacts to form validity changes
    ]).pipe(
      switchMap(([email, phone, form]) => {
        if (form.status !== 'OK' || !email || !phone) {
          return of({ email: '', phone: '' });
        }
        return combineLatest([normalizeAndHash(email), normalizeAndHash(phone)]).pipe(
          map(([emailHash, phoneHash]) => ({
            email: emailHash,
            phone: phoneHash,
          })),
          catchError((err: unknown) => {
            this.logs.warn('FormulaireRdvComponent', 'Failed to hash email/phone for GTM enhanced conversions', err);
            return of({ email: '', phone: '' });
          })
        );
      })
    ),
    { initialValue: { email: '', phone: '' } }
  );

  ngOnInit(): void {
    this.funnelStore.update('currentStep', 3);
    const storeSelected = this.storeSelected();
    const userInfo = this.userInfo();
    const appointmentFunnelStepsUrls = this.appointmentFunnel()?.urls;
    if (userInfo) {
      this.firstName.set(userInfo.firstName);
      this.lastName.set(userInfo.lastName);
      this.email.set(userInfo.email);
      this.phone.set(userInfo.phone);
      this.showNote.set(userInfo.showNote);
      this.note.set(userInfo.note);
      this.subscribeEmail.set(userInfo.subscribeEmail);
      this.subscribeSms.set(userInfo.subscribeSms);
    }
    if (storeSelected && !this.slotSelected() && appointmentFunnelStepsUrls) {
      this.navigateToStep(appointmentFunnelStepsUrls.step2);
    } else if (!storeSelected && appointmentFunnelStepsUrls) {
      this.navigateToStep(appointmentFunnelStepsUrls.step1);
    }
  }

  onInputChange($event: Event) {
    const target = $event.target as HTMLInputElement;
    const { id, value, checked } = target;
    this.logs.trace('FormulaireRdvComponent', `onInputChange - ${id}`);
    switch (id) {
      case 'firstName':
        this.firstName.set(value);
        break;
      case 'lastName':
        this.lastName.set(value);
        break;
      case 'email':
        this.email.set(value);
        break;
      case 'phone':
        this.phone.set(value);
        break;
      case 'note':
        this.note.set(value);
        break;
      case 'subscribeEmail':
        this.subscribeEmail.set(checked);
        break;
      case 'subscribeSms':
        this.subscribeSms.set(checked);
        break;
    }
  }

  onShowNote(): void {
    const showNote = this.showNote();
    if (showNote) {
      this.note.set('');
    }
    this.showNote.set(!showNote);
  }

  onSlotChange(): void {
    this.funnelStore.update('validatedStep', 1);
    this.navigateToStep(this.appointmentFunnel()!.urls.step2);
  }

  onStoreChange(): void {
    this.funnelStore.batchUpdate({
      delete: ['slotSelected', 'storeSelected'],
      update: {
        validatedStep: 0,
      },
    });
    this.navigateToStep(this.appointmentFunnel()!.urls.step1);
  }

  onSubmit(): void {
    if (this.isSending()) return;
    this.logs.debug('FormulaireRdvComponent', 'onSubmit', this.isFormValid());
    this.isFormSubmitted.set(true);
    this.serverError.set(false);
    if (this.isFormValid().status === 'KO') {
      this.scrollToFormStart();
    } else {
      this.isSending.set(true);
      const slotSelected = this.slotSelected()!;
      const storeSelected = this.storeSelected()!;
      // Final safeguard: never submit a video appointment for a store that does not support it,
      // even if a stale `visioAppointmentSelected` flag survived a store change in the funnel state.
      const isVisioAppointment = !!slotSelected.visioAppointmentSelected && !!storeSelected.isVisioAppointmentAvailable;
      if (slotSelected.visioAppointmentSelected && !storeSelected.isVisioAppointmentAvailable) {
        this.logs.warn(
          'FormulaireRdvComponent',
          'Visio appointment selected for a store that does not support it; downgrading to in-store RDV',
          { storeUuid: storeSelected.uuid }
        );
      }
      const toSaved: Omit<Rdvs, 'id'> = {
        civility: null,
        project: this.configuratorLink.read() ?? '',
        address: null,
        zipcode: null,
        city: null,
        firstName: this.firstName(),
        name: this.lastName(),
        phone: this.phone().replace(/\s/g, ''),
        email: this.email(),
        message: this.note(),
        optinEmail: this.subscribeEmail(),
        optinSms: this.subscribeSms(),
        osirisTitle: isVisioAppointment ? 'Rendez-vous visio' : 'Rendez-vous site internet',
        type: isVisioAppointment ? 'RDVVISIO' : 'RDVINT',
        start: slotSelected.formattedSlots.startDate,
        end: slotSelected.formattedSlots.endDate,
        store: `/stores/${storeSelected.uuid}`,
      };
      this.rdvsService
        .create(toSaved, true)
        .pipe(
          take(1),
          tap((rdv) => {
            this.logs.info('FormulaireRdvComponent', 'The appointment has been successfully created');
            this.configuratorLink.clear();
            const gtm = this.data.gtmConfigBtnForm;
            this.emitGtmEvent({
              event: gtm?.event,
              group: gtm?.group,
              description: gtm?.description,
              context: gtm?.context,
              button_text_value: this.data.cta.label,
              event_type: 'submit',
              user_data: {
                user_email: this.emailAndPhoneSha256().email,
                user_phone: this.emailAndPhoneSha256().phone,
              },
            });
            this.funnelStore.batchUpdate({
              update: {
                validatedStep: 3,
                rdvId: rdv.id,
                userInfo: {
                  firstName: this.firstName(),
                  lastName: this.lastName(),
                  email: this.email(),
                  phone: this.phone(),
                  showNote: this.showNote(),
                  note: this.note(),
                  subscribeEmail: this.subscribeEmail(),
                  subscribeSms: this.subscribeSms(),
                },
              },
            });
            void this.router.navigate([this.data.cta.url ?? '']);
          }),
          catchError((e) => {
            this.logs.error(
              'FormulaireRdvComponent',
              'An error occurred while requesting the creation of the appointment',
              e
            );
            this.isSending.set(false);
            this.serverError.set(true);
            return EMPTY;
          })
        )
        .subscribe();
    }
  }

  private navigateToStep(appointmentStepUrl: string): void {
    this.funnelStore.batchUpdate({
      update: {
        validatedStep: 1,
        slotSelected: this.slotSelected(),
        userInfo: {
          firstName: this.firstName(),
          lastName: this.lastName(),
          email: this.email(),
          phone: this.phone(),
          showNote: this.showNote(),
          note: this.note(),
          subscribeEmail: this.subscribeEmail(),
          subscribeSms: this.subscribeSms(),
        },
      },
    });
    if (appointmentStepUrl) {
      void this.router.navigate([appointmentStepUrl]);
    } else {
      void this.router.navigate([this.baseUrl() ?? '']);
    }
  }

  private scrollToFormStart(): void {
    const elRef = this.formStart();
    if (!elRef) {
      return;
    }
    elRef.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }
}
